/**
 * Checkout.com Flow (cards + wallets) for customer-accounts.
 *
 * - Mounts {@link https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website | Flow Web Components} via `flow-sdk`.
 * - Merchant rules (amount, billing, quote/terms) run in `validatePayment`, wired into the SDK as `setOnFormValidation`.
 * - Refs mirror latest props/checkbox state because Flow callbacks can run outside React render timing.
 *
 * Remount is limited to fields that affect the Flow session payload, including amount and billing details.
 */

import {
  useState, useEffect, useRef, useContext, useCallback,
} from 'react';
import { FlowSDK, FlowStyle } from 'flow-sdk';
import { useTranslation } from 'react-i18next';
import { CheckoutDotComAPISettings } from '../../../../api/CheckoutDotComAPI';
import PaymentContext from '../../../../context/PaymentContext';
import styles from './FlowPayment.module.scss';
import '../../../../translations/i18n';
import * as utils from '../../../../utils/OrderUtils';
import { getAppConfig, getCountryCodes, getLangPrefixFromBrand } from '../../../../config/config';
import Payments from '../../../Analytics/Payments/Payments';
import Checkboxes from '../Checkboxes/Checkboxes';
import updateOrderDetails from '../../../../api/UpdateOrderDetails';
import addAdminComment from '../../../../api/AddAdminComment';
import addOrderLog from '../../../../api/AddOrderLog';
import * as constants from '../../../../constants/constants';

function billingDisplayName(order) {
  return `${order.billing_address_fname || ''} ${order.billing_address_lname || ''}`.trim();
}

function flowAddressPayload(order) {
  return {
    address_line_1: order.billing_address_street1 || '',
    address_line_2: order.billing_address_street2 || '',
    city: order.billing_address_city || '',
    postcode: order.billing_address_postal_code || '',
    country: order.billing_address_country || '',
  };
}

function flowPhonePayload(order, phonePrefix) {
  return {
    phone_number: order.billing_address_phone || '',
    phone_number_prefix: `+${phonePrefix}`,
  };
}

export function cleanAmount(value) {
  return String(value ?? '').replace(/,/g, '');
}

export function toNumericAmount(value) {
  return Number(cleanAmount(value));
}

function remainingBalanceAfterPayment(balance, paidAmount) {
  const balanceInCents = Number(utils.getAmount(cleanAmount(balance)));
  const paidAmountInCents = Number(utils.getAmount(cleanAmount(paidAmount)));
  return ((balanceInCents - paidAmountInCents) / 100).toFixed(2);
}

export function isOrderFullPayment(order, payAmount, isCustomerPayingNonRefundableDeposit = false) {
  if (isCustomerPayingNonRefundableDeposit || !order?.balance || !payAmount) {
    return false;
  }
  const balanceInCents = Number(utils.getAmount(cleanAmount(order.balance)));
  const payAmountInCents = Number(utils.getAmount(cleanAmount(payAmount)));
  return balanceInCents > 0 && payAmountInCents >= balanceInCents;
}

export function isKlarnaPaymentEnabled(klarna, isFullPayment, payAmount, order) {
  if (utils.getCookieValue('klarna_enabled') !== '1') return false;

  if (!isFullPayment || !klarna) {
    return false;
  }

  const isEnabled = Boolean(Number(klarna.enabled));
  if (!isEnabled) {
    return false;
  }

  const paymentMethods = order?.payment_methods;

  if (
    !Array.isArray(paymentMethods)
    || !paymentMethods.some((method) => method?.id === 'klarna')
  ) {
    return false;
  }

  const numericPayAmount = toNumericAmount(payAmount);
  if (Number.isNaN(numericPayAmount) || numericPayAmount <= 0) {
    return false;
  }

  const minRaw = klarna.minimum_spent_amount;
  const maxRaw = klarna.maximum_spent_amount;

  if (
    minRaw === null || minRaw === '' || minRaw === undefined
    || maxRaw === null || maxRaw === '' || maxRaw === undefined
  ) {
    return false;
  }

  const minimumSpentAmount = Number(minRaw);
  const maximumSpentAmount = Number(maxRaw);

  if (Number.isNaN(minimumSpentAmount) || Number.isNaN(maximumSpentAmount)) {
    return false;
  }

  const meetsMinimum = minimumSpentAmount <= 0 || numericPayAmount >= minimumSpentAmount;
  const meetsMaximum = maximumSpentAmount <= 0 || numericPayAmount <= maximumSpentAmount;

  return meetsMinimum && meetsMaximum;
}

// Request geo param (e.g. "de-de"), not URL prefix ("de") from request_geo_params.
function flowSdkGeoParam(brand) {
  return getLangPrefixFromBrand(brand);
}

function exposeFlowOnWindowForDev(flow) {
  if (process.env.NODE_ENV === 'development') {
    window.flowSDK = flow;
  }
}

function clearFlowFromWindowInDev(flow) {
  if (process.env.NODE_ENV === 'development' && window.flowSDK === flow) {
    delete window.flowSDK;
  }
}

function FlowPayment(props) {
  const { t } = useTranslation('common');
  const {
    order,
    payAmount,
    isBillingDetailsValid,
    isValidAmount,
    isCustomerPayingNonRefundableDeposit,
  } = props;

  const config = getAppConfig();
  const countryCodes = getCountryCodes();

  const [checkoutDotComPublicKey, setCheckoutDotComPublicKey] = useState('');
  const [checkoutDotComSettings, setCheckoutDotComSettings] = useState(null);
  const [confirmQuoteChecked, setConfirmQuoteChecked] = useState(false);
  const [confirmTermsChecked, setConfirmTermsChecked] = useState(
    order?.agree_terms === constants.ORDER_AGREEMENT_OPT_IN,
  );
  const [receiveOffersChecked, setReceiveOffersChecked] = useState(
    order?.agree_marketing === constants.ORDER_AGREEMENT_OPT_IN,
  );
  const [failureMessage, setFailureMessage] = useState('');
  const [isFlowReady, setIsFlowReady] = useState(false);

  const paymentCtx = useContext(PaymentContext);
  const { paymentData } = paymentCtx;

  const confirmQuoteRef = useRef(confirmQuoteChecked);
  const confirmTermsRef = useRef(confirmTermsChecked);
  const receiveOffersRef = useRef(receiveOffersChecked);
  const isValidAmountRef = useRef(isValidAmount);
  const isBillingDetailsValidRef = useRef(isBillingDetailsValid);
  const payAmountRef = useRef(payAmount);

  useEffect(() => {
    confirmQuoteRef.current = confirmQuoteChecked;
    confirmTermsRef.current = confirmTermsChecked;
    receiveOffersRef.current = receiveOffersChecked;
    isValidAmountRef.current = isValidAmount;
    isBillingDetailsValidRef.current = isBillingDetailsValid;
    payAmountRef.current = payAmount;
  }, [
    confirmQuoteChecked,
    confirmTermsChecked,
    receiveOffersChecked,
    isValidAmount,
    isBillingDetailsValid,
    payAmount,
  ]);

  const brandColours = {
    ATD: '#00AD48',
    UO: '#0368d9',
    SW: '#0076c6',
  };

  const currentBrand = config?.brand;
  const ctaBackground = brandColours[currentBrand] ?? '#00AD48';

  useEffect(() => {
    document.documentElement.style.setProperty('--dynamic-checkout-color', ctaBackground);
  }, [ctaBackground]);

  useEffect(() => {
    CheckoutDotComAPISettings()
      .then((result) => {
        if (result?.public_key) {
          setCheckoutDotComPublicKey(result.public_key);
        }
        setCheckoutDotComSettings(result);
      })
      .catch((error) => {
        console.error('CheckoutDotComAPI - Failed to fetch settings.', error);
      });
  }, []);

  const validatePayment = useCallback((component) => {
    const reject = (translationKey) => {
      const message = t(translationKey);
      if (component?.type && component.type !== 'card') {
        setFailureMessage(message);
      }
      return message;
    };

    if (!isValidAmountRef.current) return reject('payment.invalid_amount');
    if (!isBillingDetailsValidRef.current) return reject('payment.invalid_billing');
    if (order?.status === constants.ORDER_STATUS.QUOTE && !confirmQuoteRef.current) {
      return reject('payment.confirm_quote_required');
    }
    if (!confirmTermsRef.current) return reject('payment.terms_required');

    setFailureMessage('');
    return true;
  }, [order?.status, t]);

  useEffect(() => {
    if (!checkoutDotComPublicKey || !order?.id) return undefined;

    setIsFlowReady(false);

    const env = utils.getEnv();
    const sdkEnv = env === 'production' ? constants.FLOW_SDK_ENV.LIVE : constants.FLOW_SDK_ENV.SANDBOX;
    const lang = utils.getLang(config.brand);
    const geoParam = flowSdkGeoParam(config.brand);
    const phonePrefix = countryCodes[lang] || constants.FLOW_CHECKOUT.DEFAULT_PHONE_PREFIX;
    const basePath = window.location.origin;
    const displayName = billingDisplayName(order);
    const locale = constants.FLOW_LOCALE_BY_BRAND_LANG[lang] || constants.FLOW_DEFAULT_LOCALE;

    const clearFailureAndSyncOrderDetails = () => {
      setFailureMessage('');
      updateOrderDetails(
        order.id,
        order.owner,
        confirmQuoteRef.current,
        receiveOffersRef.current,
        confirmTermsRef.current,
      ).catch(() => {});
    };

    const onPaymentFailed = () => {
      paymentData(constants.PAYMENT_STATUS.FAILED, payAmountRef.current);
      setFailureMessage(t('payment.payment_failed'));
    };

    const isFullPayment = isOrderFullPayment(
      order,
      payAmount,
      isCustomerPayingNonRefundableDeposit,
    );

    const isKlarnaEnabled = isKlarnaPaymentEnabled(
      checkoutDotComSettings?.klarna,
      isFullPayment,
      payAmount,
      order,
    );

    const disabledPaymentMethods = isKlarnaEnabled
      ? constants.FLOW_DISABLED_PAYMENT_METHODS.filter((method) => method !== 'klarna')
      : [...constants.FLOW_DISABLED_PAYMENT_METHODS];

    const numericPayAmount = toNumericAmount(payAmount) || 0;

    if (numericPayAmount <= 0) {
      return undefined;
    }

    const flow = new FlowSDK()
      .setPublicKey(checkoutDotComPublicKey)
      .setEnv(sdkEnv)
      .setLocale(locale)
      .setBasePath(basePath)
      .setMountId(constants.FLOW_CHECKOUT.MOUNT_ELEMENT_ID)
      .setContext(constants.FLOW_CHECKOUT.CONTEXT)
      .setResolveBillingCountryToIso(true)
      .setDisabledPaymentMethods(disabledPaymentMethods)
      .setAmount(numericPayAmount)
      .setCurrency(order.currency)
      .setOrderId(String(order.id))
      .setEmail(order.email)
      .setDisplayName(displayName)
      .setAddress(flowAddressPayload(order))
      .setPhone(flowPhonePayload(order, phonePrefix))
      .setRememberMe(true)
      .setOnFormValidation((component) => validatePayment(component))
      .setOnCardSubmit(() => {
        clearFailureAndSyncOrderDetails();
      })
      .setOnAPMSubmit(async () => {
        clearFailureAndSyncOrderDetails();
        return true;
      })
      .setOnReady(() => {
        setFailureMessage('');
        setIsFlowReady(true);
      })
      .setOnAPMsPaymentCompleted(() => {
        const amountLeftToPay = remainingBalanceAfterPayment(order.balance, payAmountRef.current);

        Payments(order, payAmountRef.current, constants.FLOW_CHECKOUT.ANALYTICS_PAYMENT_TYPE);

        if (isCustomerPayingNonRefundableDeposit) {
          const comment = `<b style="color: red;">NON REFUNDABLE DEPOSIT WAS SELECTED! AMOUNT: ${utils.formatPrice(order.currency, payAmountRef.current)}. THIS AMOUNT SHOULDN'T BE REFUNDED!</b>`;
          addAdminComment(order.id, comment, order.owner);
          addOrderLog(order.id, comment);
        }

        paymentData(constants.PAYMENT_STATUS.SUCCESS, amountLeftToPay);
      })
      .setOnPaymentDeclined(onPaymentFailed)
      .setOnPaymentDeclinedInvalidCustomerData(onPaymentFailed)
      .setOnPaymentSessionExpired(() => {
        setFailureMessage(t('payment.session_expired'));
      });

    if (geoParam) {
      flow.setGeo(geoParam);
    }

    flow.build().catch((error) => {
      console.error('FlowSDK - Failed to build payment flow', error);
      setFailureMessage(t('payment.payment_failed'));
    });
    exposeFlowOnWindowForDev(flow);

    return () => {
      clearFlowFromWindowInDev(flow);
      flow.unmount();
    };
  }, [
    checkoutDotComPublicKey,
    checkoutDotComSettings,
    isCustomerPayingNonRefundableDeposit,
    order?.balance,
    order?.billing_address_city,
    order?.billing_address_country,
    order?.billing_address_fname,
    order?.billing_address_lname,
    order?.billing_address_phone,
    order?.billing_address_postal_code,
    order?.billing_address_street1,
    order?.billing_address_street2,
    order?.currency,
    order?.email,
    order?.id,
    order?.owner,
    payAmount,
    validatePayment,
  ]);

  return (
    <div className={styles.flowPayment}>
      <div className={styles.checkboxes}>
        <Checkboxes
          order={order}
          onConfirmQuoteChange={setConfirmQuoteChecked}
          onConfirmTermsChange={setConfirmTermsChecked}
          onReceiveOffersChange={setReceiveOffersChecked}
          confirmQuoteChecked={confirmQuoteChecked}
          confirmTermsChecked={confirmTermsChecked}
          receiveOffersChecked={receiveOffersChecked}
        />
      </div>
      <div
        className={styles.flowMountWrap}
        aria-busy={!isFlowReady}
      >
        {!isFlowReady && (
          <div className={styles.loading_overlay} aria-hidden>
            <div className={styles.loading_circle} />
          </div>
        )}
        <div
          id={constants.FLOW_CHECKOUT.MOUNT_ELEMENT_ID}
          className={styles.flowContainer}
        />
      </div>
      {failureMessage && (
        <p className={styles.error}>{failureMessage}</p>
      )}
    </div>
  );
}

export default FlowPayment;
